import { useEffect, useRef, useState } from 'react';

import { loadBunnyPlayerJs } from '../../../utils/BunnyPlayerJs';

// Fração do vídeo que conta como "assistido". 90% em vez de 100% porque quase ninguém
// deixa correr os créditos finais, e exigir o fim exato deixaria o tutorial marcado como
// pendente para sempre.
const WATCHED_THRESHOLD = 0.9;

/**
 * Player do tutorial selecionado.
 *
 * O visual do player (cor dos controles, fonte) é configurado no painel do Bunny, por
 * biblioteca — não há parâmetro de cor na URL do embed. Aqui cuidamos da moldura: o
 * contêiner usa os tokens do tema, então o player fica dentro de uma superfície que
 * acompanha o modo claro e o escuro.
 *
 * O Player.js serve para saber o progresso: é o que permite marcar o tutorial como
 * assistido e oferecer o próximo. Se a biblioteca não carregar, o vídeo continua
 * funcionando; só o acompanhamento deixa de existir.
 *
 * @param {object} video          tutorial em exibição
 * @param {Function} onWatched    chamado uma vez quando o vídeo passa do limite de assistido
 */
const TutorialPlayer = ({ video, onWatched }) => {

    const iframeRef = useRef(null);

    // Evita disparar onWatched a cada evento de progresso depois do limite.
    const reportedRef = useRef(false);

    const [playerJsFailed, setPlayerJsFailed] = useState(false);

    useEffect(() => {
        reportedRef.current = false;
        setPlayerJsFailed(false);

        // Guarda contra atualização depois do desmonte: o script é assíncrono e o usuário
        // pode trocar de vídeo antes de ele chegar.
        let cancelled = false;
        let player = null;

        loadBunnyPlayerJs()
            .then((playerjs) => {
                if (cancelled || !iframeRef.current) return;

                player = new playerjs.Player(iframeRef.current);

                player.on('ready', () => {
                    if (cancelled) return;

                    player.on('timeupdate', ({ seconds, duration }) => {
                        if (reportedRef.current || !duration) return;

                        if (seconds / duration >= WATCHED_THRESHOLD) {
                            reportedRef.current = true;
                            onWatched?.(video.id);
                        }
                    });

                    // Vídeo curto pode terminar sem passar pelo limite de progresso, então o
                    // fim também conclui o tutorial.
                    player.on('ended', () => {
                        if (reportedRef.current) return;

                        reportedRef.current = true;
                        onWatched?.(video.id);
                    });
                });
            })
            .catch(() => {
                if (!cancelled) setPlayerJsFailed(true);
            });

        return () => {
            cancelled = true;
            // O Player.js não expõe destroy; remover os ouvintes basta, e o iframe é
            // recriado pelo key do elemento quando o vídeo muda.
            try {
                player?.off('timeupdate');
                player?.off('ended');
            } catch (error) {
                // Player já descartado junto com o iframe: nada a limpar.
            }
        };
    }, [video.id, onWatched]);

    return (
        <div className="help-center__player">
            <div className="help-center__player-frame">
                <iframe
                    key={video.id}
                    ref={iframeRef}
                    src={video.embedUrl}
                    title={video.title}
                    loading="lazy"
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                ></iframe>
            </div>

            {playerJsFailed && (
                <p className="help-center__player-note">
                    <i className="fa-solid fa-circle-info me-2"></i>
                    O vídeo funciona normalmente, mas não foi possível acompanhar o progresso neste
                    navegador. Use o botão de concluído se quiser marcar o tutorial.
                </p>
            )}
        </div>
    );
};

export default TutorialPlayer;
